/**
 * Status chip counts for the work-order list (#2881).
 *
 * The "all" chip shows the stats route's `totalCount`, which counts every row
 * whatever its status. The status chips only exist for the tenant's enabled
 * flow statuses, so a row in a legacy status (`confirmed`, `paid`) or a status
 * the tenant has since disabled was counted in the total with no chip of its
 * own — the chips summed to less than the total and the work order looked lost.
 *
 * `partitionStatusCounts` splits the stats into the counts the flow chips show
 * and the remainder, so the remainder can get a chip of its own. Invariant:
 * the sum of `listed` plus `other.count` equals `totalCount`.
 */

/** A `byStatus` entry from `GET /work-orders/stats`. */
export interface StatusCountEntry {
  status: string;
  count: number;
}

export interface PartitionedStatusCounts {
  /** Count per enabled status key; every enabled key is present (0 when absent). */
  listed: Record<string, number>;
  /** Rows whose status has no chip. `statuses` lists only statuses with rows. */
  other: { statuses: string[]; count: number };
}

/**
 * The stats route appends `waiting_parts` after the real per-status rows. It
 * counts the `isWaitingForParts` flag, which sits alongside the row's real
 * status, so it is not part of `totalCount` (#2154).
 */
export const WAITING_PARTS_STATUS = 'waiting_parts';

/** Selection key for the "other statuses" chip. Never a real status. */
export const OTHER_STATUSES_KEY = 'other';

export function partitionStatusCounts(
  byStatus: readonly StatusCountEntry[],
  enabledKeys: readonly string[],
): PartitionedStatusCounts {
  // Only the last waiting_parts entry is the synthetic flag count; a real row
  // still carrying the legacy status `waiting_parts` comes before it and is a
  // status like any other.
  let syntheticIndex = -1;
  for (let i = byStatus.length - 1; i >= 0; i -= 1) {
    if (byStatus[i]?.status === WAITING_PARTS_STATUS) {
      syntheticIndex = i;
      break;
    }
  }

  const enabled = new Set(enabledKeys);
  const listed: Record<string, number> = {};
  for (const key of enabledKeys) listed[key] = 0;

  const otherCounts = new Map<string, number>();
  byStatus.forEach((entry, index) => {
    if (index === syntheticIndex) return;
    if (enabled.has(entry.status)) {
      listed[entry.status] = (listed[entry.status] ?? 0) + entry.count;
    } else if (entry.count > 0) {
      otherCounts.set(entry.status, (otherCounts.get(entry.status) ?? 0) + entry.count);
    }
  });

  let otherCount = 0;
  for (const count of otherCounts.values()) otherCount += count;

  return {
    listed,
    other: { statuses: [...otherCounts.keys()], count: otherCount },
  };
}
